import * as React from "react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { FieldRow, LabeledInput, LabeledSelect } from "../formPrimitives";
import {
  KONTAKT_SUCHE_MIN_ZEICHEN,
  sucheKontakteFuerZuweisung,
  type KontaktTreffer,
} from "../../../services/kontaktSucheService";
import type { ContactRef } from "../../relationsInline/types";
import { refDisplayName } from "../../relationsInline/types";
import { geschlechtAusAnrede } from "../../../utils/anredeGeschlecht";
import { PhoneInput } from "./PhoneInput";
import { VollanlageWeiterKnopf } from "../VollanlageWeiter";
import { alleDokumente } from "../../windowManager/browserFenster/fensterRegister";

const SearchIcon = (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" style={{ flexShrink: 0 }}>
    <circle cx="9" cy="9" r="6" />
    <path d="M14 14l3.5 3.5" />
  </svg>
);

const PersonIcon = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <circle cx="8" cy="5" r="3" />
    <path d="M2.5 14a5.5 5.5 0 0 1 11 0" />
  </svg>
);

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
    position: "relative",
  },
  label: {
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.875rem",
    color: REFINED.ink,
  },
  searchWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  searchIcon: {
    position: "absolute",
    left: "12px",
    color: REFINED.ink3,
    display: "flex",
    alignItems: "center",
    pointerEvents: "none",
  },
  searchInput: {
    width: "100%",
    boxSizing: "border-box",
    minHeight: "42px",
    paddingLeft: "36px",
    paddingRight: "12px",
    backgroundColor: REFINED.white,
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    fontFamily: REFINED.fontUI,
    fontSize: "0.92rem",
    color: REFINED.ink,
    outlineStyle: "none",
    transitionProperty: "border-color, box-shadow",
    transitionDuration: "140ms",
    ":focus": {
      ...shorthands.borderColor(REFINED.aub),
      boxShadow: `0 0 0 2px ${REFINED.aub50}`,
    },
    "::placeholder": {
      color: REFINED.ink3,
    },
  },
  resultsDropdown: {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    zIndex: 1000,
    backgroundColor: REFINED.white,
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    boxShadow: "0 6px 20px rgba(58,12,49,0.12)",
    maxHeight: "260px",
    overflowY: "auto",
    padding: "4px",
    margin: 0,
    listStyleType: "none",
  },
  resultRow: {
    width: "100%",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 12px",
    backgroundColor: "transparent",
    ...shorthands.borderWidth("0"),
    ...shorthands.borderRadius("8px"),
    cursor: "pointer",
    textAlign: "left",
    fontFamily: REFINED.fontUI,
    ":hover": {
      backgroundColor: REFINED.aub50,
    },
  },
  resultName: {
    fontWeight: 600,
    fontSize: "0.9rem",
    color: REFINED.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  resultBadge: {
    fontSize: "0.72rem",
    fontWeight: 700,
    color: REFINED.aub,
    backgroundColor: REFINED.aub50,
    ...shorthands.border("1px", "solid", "#e8d2e2"),
    ...shorthands.borderRadius("999px"),
    padding: "1px 8px",
    marginLeft: "8px",
    flexShrink: 0,
  },
  newOptionBtn: {
    width: "100%",
    boxSizing: "border-box",
    padding: "9px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundColor: "#faf8f5",
    color: REFINED.aub,
    fontWeight: 600,
    fontSize: "0.86rem",
    fontFamily: REFINED.fontUI,
    ...shorthands.borderWidth("0"),
    borderTop: `1px solid ${REFINED.line}`,
    cursor: "pointer",
    ":hover": {
      backgroundColor: REFINED.aub50,
      textDecoration: "underline",
    },
  },
  selectedCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "9px 14px",
    backgroundColor: REFINED.white,
    ...shorthands.border("1.5px", "solid", REFINED.aub),
    ...shorthands.borderRadius("10px"),
    boxSizing: "border-box",
  },
  selectedInfo: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    minWidth: 0,
  },
  selectedText: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.92rem",
    fontWeight: 700,
    color: REFINED.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  changeBtn: {
    background: "none",
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("6px"),
    color: REFINED.ink2,
    fontFamily: REFINED.fontUI,
    fontSize: "0.8rem",
    fontWeight: 600,
    padding: "3px 9px",
    cursor: "pointer",
    ":hover": {
      backgroundColor: REFINED.fieldBg,
      color: REFINED.ink,
    },
  },
  inlineForm: {
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
    padding: "14px",
    backgroundColor: "#faf8f5",
    ...shorthands.border("1px", "solid", "#d4c29d"),
    ...shorthands.borderRadius("10px"),
  },
  switchBackBtn: {
    alignSelf: "flex-start",
    background: "none",
    border: "none",
    padding: 0,
    color: REFINED.aub,
    fontFamily: REFINED.fontUI,
    fontSize: "0.82rem",
    fontWeight: 600,
    cursor: "pointer",
    marginTop: "2px",
    ":hover": {
      textDecoration: "underline",
    },
  },
  emptyNotice: {
    padding: "8px 12px",
    fontSize: "0.84rem",
    fontFamily: REFINED.fontUI,
    color: REFINED.ink3,
  },
});

export const PersonSuchfeldMitNeu: React.FC<{
  label?: string;
  name?: string;
  placeholder?: string;
  value: ContactRef | null;
  onChange: (val: ContactRef | null) => void;
}> = ({ label, name, placeholder = "Person suchen …", value, onChange }) => {
  const s = useStyles();
  const [searchTerm, setSearchTerm] = React.useState("");
  const [results, setResults] = React.useState<KontaktTreffer[]>([]);
  const [searching, setSearching] = React.useState(false);
  const [openDropdown, setOpenDropdown] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Debounced search
  React.useEffect(() => {
    const trimmed = searchTerm.trim();
    if (trimmed.length < KONTAKT_SUCHE_MIN_ZEICHEN) {
      setResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const treffer = await sucheKontakteFuerZuweisung(trimmed, { typ: "person" });
        setResults(treffer);
        setOpenDropdown(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 280);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Click outside closes dropdown
  React.useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpenDropdown(false);
      }
    };
    alleDokumente.addEventListener("mousedown", handleOutside);
    return () => alleDokumente.removeEventListener("mousedown", handleOutside);
  }, []);

  const handleSelectContact = (c: KontaktTreffer) => {
    onChange({
      contactId: c.contactId,
      type: "Person",
      displayName: c.displayName,
      lastName: c.displayName,
      salutationLabel: undefined,
    });
    setSearchTerm("");
    setResults([]);
    setOpenDropdown(false);
  };

  const handleStartManual = () => {
    const trimmed = searchTerm.trim();
    let firstName = "";
    let lastName = trimmed;
    if (trimmed.includes(",")) {
      const parts = trimmed.split(",").map((p) => p.trim());
      lastName = parts[0] || "";
      firstName = parts.slice(1).join(" ");
    } else if (trimmed.includes(" ")) {
      const parts = trimmed.split(/\s+/);
      firstName = parts[0] || "";
      lastName = parts.slice(1).join(" ");
    }

    onChange({
      type: "Person",
      manual: true,
      firstName,
      lastName,
    });
    setSearchTerm("");
    setResults([]);
    setOpenDropdown(false);
  };

  const patchManual = (patch: Partial<ContactRef>) => {
    if (!value) return;
    onChange({ ...value, ...patch });
  };

  // ── Modus A: Bestehender Kontakt gewählt ──
  if (value && !value.manual && (value.contactId || value.displayName)) {
    return (
      <div className={s.container}>
        {label && <label className={s.label}>{label}</label>}
        <div className={s.selectedCard}>
          <div className={s.selectedInfo}>
            <span style={{ color: REFINED.aub, display: "flex", alignItems: "center" }}>
              {PersonIcon}
            </span>
            <span className={s.selectedText}>
              {refDisplayName(value) || value.displayName || "Person"}
            </span>
            <span className={s.resultBadge}>Person</span>
          </div>
          <button
            type="button"
            className={s.changeBtn}
            onClick={() => {
              onChange(null);
              setSearchTerm("");
            }}
          >
            Ändern
          </button>
        </div>
      </div>
    );
  }

  // ── Modus B: Freie Inline-Neuanlage ──
  if (value && value.manual) {
    const idBase = name || "person-neu";
    return (
      <div className={s.container}>
        {label && <label className={s.label}>{label}</label>}
        <div className={s.inlineForm}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontWeight: 700, fontSize: "0.88rem", color: REFINED.aub }}>
              + Neuer Kontakt (wird beim Speichern angelegt)
            </span>
            <button
              type="button"
              className={s.switchBackBtn}
              onClick={() => onChange(null)}
            >
              ← stattdessen bestehenden Kontakt suchen
            </button>
          </div>

          <FieldRow columns={2}>
            <LabeledSelect
              label="Anrede"
              name={`${idBase}-anrede`}
              value={value.salutationLabel || ""}
              onChange={(v) => {
                const sal = v || undefined;
                patchManual({
                  salutationLabel: sal,
                  gender: geschlechtAusAnrede(sal) ?? value.gender,
                });
              }}
              options={[
                { value: "Herr", label: "Herr" },
                { value: "Frau", label: "Frau" },
              ]}
              placeholder="— Keine —"
            />
            <LabeledInput
              label="Titel"
              name={`${idBase}-titel`}
              value={value.titleLabel || ""}
              onChange={(v) => patchManual({ titleLabel: v })}
              placeholder="z. B. Dr., Prof."
            />
          </FieldRow>

          <FieldRow columns={2}>
            <LabeledInput
              label="Vorname"
              name={`${idBase}-vorname`}
              value={value.firstName || ""}
              onChange={(v) => patchManual({ firstName: v })}
              placeholder="Vorname"
            />
            <LabeledInput
              label="Nachname"
              name={`${idBase}-nachname`}
              required
              value={value.lastName || ""}
              onChange={(v) => patchManual({ lastName: v })}
              placeholder="Nachname"
            />
          </FieldRow>

          <FieldRow columns={2}>
            <PhoneInput actionButton={false} kind="landline"
              label="Telefon (privat)"
              name={`${idBase}-tel-p`}
              value={value.phonePrivate || ""}
              onChange={(v) => patchManual({ phonePrivate: v })}
            />
            <PhoneInput actionButton={false} kind="landline" geschaeftlich
              label="Telefon (geschäftlich)"
              name={`${idBase}-tel-g`}
              value={value.phoneWork || ""}
              onChange={(v) => patchManual({ phoneWork: v })}
            />
          </FieldRow>

          <FieldRow columns={2}>
            <PhoneInput actionButton={false} kind="mobile"
              label="Mobil (privat)"
              name={`${idBase}-mob-p`}
              value={value.mobilePrivate || ""}
              onChange={(v) => patchManual({ mobilePrivate: v })}
            />
            <PhoneInput actionButton={false} kind="mobile" geschaeftlich
              label="Mobil (geschäftlich)"
              name={`${idBase}-mob-g`}
              value={value.mobileWork || ""}
              onChange={(v) => patchManual({ mobileWork: v })}
            />
          </FieldRow>

          <FieldRow columns={2}>
            <LabeledInput
              label="E-Mail (privat)"
              name={`${idBase}-mail-p`}
              value={value.emailPrivate || ""}
              onChange={(v) => patchManual({ emailPrivate: v })}
            />
            <LabeledInput
              label="E-Mail (geschäftlich)"
              name={`${idBase}-mail-g`}
              value={value.emailWork || ""}
              onChange={(v) => patchManual({ emailWork: v })}
            />
          </FieldRow>

          {/* Vollanlage (User 02.10.2026): danach ein bestehender Kontakt. */}
          <VollanlageWeiterKnopf kontakt={value} onCreated={({ ref }) => onChange(ref)} />
        </div>
      </div>
    );
  }

  // ── Modus C: Sucheingabe ──
  return (
    <div className={s.container} ref={containerRef}>
      {label && <label className={s.label}>{label}</label>}
      <div className={s.searchWrap}>
        <span className={s.searchIcon}>{SearchIcon}</span>
        <input
          type="text"
          name={name}
          className={s.searchInput}
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => {
            if (searchTerm.trim().length >= KONTAKT_SUCHE_MIN_ZEICHEN) {
              setOpenDropdown(true);
            }
          }}
        />
      </div>

      {openDropdown && (
        <div className={s.resultsDropdown}>
          {searching && <div className={s.emptyNotice}>Suche läuft…</div>}

          {!searching && results.length === 0 && (
            <div className={s.emptyNotice}>Keine Personen gefunden.</div>
          )}

          {!searching &&
            results.map((c) => (
              <button
                type="button"
                key={c.contactId}
                className={s.resultRow}
                onClick={() => handleSelectContact(c)}
              >
                <span className={s.resultName}>{c.displayName}</span>
                <span className={s.resultBadge}>Person</span>
              </button>
            ))}

          <button
            type="button"
            className={s.newOptionBtn}
            onClick={handleStartManual}
          >
            + Kontakt ist noch nicht im System — neu eintippen
          </button>
        </div>
      )}

      {/* Button immer unter dem Suchfeld sichtbar, wenn kein Dropdown offen ist */}
      {!openDropdown && (
        <button
          type="button"
          className={s.switchBackBtn}
          onClick={handleStartManual}
        >
          + Kontakt ist noch nicht im System — neu eintippen
        </button>
      )}
    </div>
  );
};
